import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pencil, Trash2, X, Save, Phone, MapPin, User, CheckCircle2, XCircle } from 'lucide-react';
import { AppointmentDocuments } from './AppointmentDocuments';
import { TrafficLight } from '@/components/ui/TrafficLight';
import { useUpdateProperty } from '@/hooks/data/useProperties';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { SearchablePicker } from '@/components/ui/SearchablePicker';
import { fieldLabel, valueLabel } from '@/lib/historyLabels';
import {
  useAppointmentHistory,
  useArchiveAppointment,
  useUpdateAppointment,
  type AppointmentRow,
} from '@/hooks/data/useAppointments';
import { useAgents } from '@/hooks/data/useAgents';
import { useClients } from '@/hooks/data/useClients';
import { useProperties, type PropertyRow } from '@/hooks/data/useProperties';
import { useAuth } from '@/hooks/useAuth';
import { useConfirm } from '@/components/ui/confirm';
import { toMs } from '@/lib/appointmentDate';
import { isMaskedPhoneClient } from '@/lib/phoneDisplay';
import { formatDateTime } from '@/lib/formatLocale';

interface Props {
  appointment: AppointmentRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TYPE_OPTIONS: AppointmentRow['appointment_type'][] = [
  'aufnahme',
  'besichtigung',
  'kaufanbot',
  'notar',
  'sonstiges',
];
const STATUS_OPTIONS: AppointmentRow['status'][] = [
  'open',
  'pending',
  'confirmed',
  'completed',
  'cancelled',
  'rejected',
  'signed_contract',
];

// Full property marketing-stage lifecycle — exposed directly on the
// appointment so the agent can advance the linked property without opening
// it separately (tester feedback 2026-06-29 #3). `sold` is legacy-only and
// omitted; `notariell_verkauft` is the canonical final state.
const PROPERTY_STAGE_OPTIONS = [
  'lead',
  'aufnahme',
  'active',
  'pending',
  'kaufanbot_angenommen',
  'notar_termin_geplant',
  'notariell_verkauft',
] as const;

function toLocalInput(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function AppointmentDetailsDialog({ appointment, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const confirm = useConfirm();
  const update = useUpdateAppointment(appointment.id);
  const archive = useArchiveAppointment();
  const { data: agents = [] } = useAgents();
  const { data: clients = [] } = useClients({ limit: 10000 });
  const { data: properties = [] } = useProperties({ limit: 10000 });

  const [editing, setEditing] = useState(false);
  const [showAuftragForm, setShowAuftragForm] = useState(false);
  const [ediInput, setEdiInput] = useState('');
  const [form, setForm] = useState(() => ({
    appointmentType: appointment.appointment_type,
    status: appointment.status,
    appointmentDate: toLocalInput(toMs(appointment.appointment_date)),
    notes: appointment.notes ?? '',
    agentProfileId: appointment.agent_id ?? '',
    propertyId: appointment.property_id ?? '',
  }));

  const updateProperty = useUpdateProperty(appointment.property_id ?? '');
  const { data: history = [] } = useAppointmentHistory(open ? appointment.id : undefined);

  const client = useMemo(
    () => (appointment.client_id ? clients.find((c) => c.id === appointment.client_id) : null),
    [clients, appointment.client_id],
  );
  const property = useMemo(
    () => (appointment.property_id ? properties.find((p) => p.id === appointment.property_id) : null),
    [properties, appointment.property_id],
  );

  // Owner check mirrors the backend assertOwnerOrAdmin guard so the UI
  // hides actions the user can't actually execute.
  const isAdmin = profile?.role === 'admin';
  const isOwner =
    profile != null &&
    (appointment.agent_id === profile.id || appointment.created_by === profile.user_id);
  const canEdit = isAdmin || isOwner;

  const onSave = async () => {
    try {
      const ms = new Date(form.appointmentDate).getTime();
      if (!Number.isFinite(ms)) {
        toast.error(t('appointments.errors.invalid_date', 'Ungültiges Datum.'));
        return;
      }
      await update.mutateAsync({
        appointmentType: form.appointmentType,
        status: form.status,
        appointmentDate: ms,
        notes: form.notes.trim() || null,
        ...(isAdmin
          ? { agentProfileId: form.agentProfileId || null }
          : {}),
        propertyId: form.propertyId || null,
      });
      toast.success(t('appointments.update.success', 'Termin aktualisiert.'));
      setEditing(false);
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  // "Auftrag bekommen" — single-click contract win.
  // Sets status=signed_contract (→ green light). If user provided an
  // EDI number AND a property is linked, also patches the property's
  // edi_real_number so the value lives where the rest of the CRM looks
  // for it.
  const onAuftragBekommen = async () => {
    try {
      const edi = ediInput.trim();
      await update.mutateAsync({ status: 'signed_contract' });
      if (edi && appointment.property_id) {
        await updateProperty.mutateAsync({ ediRealNumber: edi });
      }
      toast.success(
        t('appointments.actions.auftrag_bekommen_success', 'Auftrag erfasst — Status auf Grün.'),
      );
      setShowAuftragForm(false);
      setEdiInput('');
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  // Revert a closed case back to "open" — for when a contract gets
  // cancelled or recorded by mistake. Any prior status flip is already
  // in appointment_history; this one too.
  const onAuftragZuruecksetzen = async () => {
    const ok = await confirm({
      title: t('appointments.auftrag.revert_title', 'Status zurücksetzen?'),
      body: t(
        'appointments.auftrag.revert_body',
        'Der Status wird auf „offen" zurückgesetzt. Die Ampel springt entsprechend (Off / Gelb je nach Termindatum). Die Änderung wird protokolliert.',
      ),
      confirmLabel: t('appointments.auftrag.revert', 'Status zurücksetzen'),
    });
    if (!ok) return;
    try {
      await update.mutateAsync({ status: 'open' });
      toast.success(
        t('appointments.auftrag.revert_success', 'Status zurückgesetzt.'),
      );
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  const onAuftragNichtBekommen = async () => {
    const ok = await confirm({
      title: t('appointments.actions.auftrag_nicht_bekommen_title', 'Auftrag nicht bekommen?'),
      body: t(
        'appointments.actions.auftrag_nicht_bekommen_body',
        'Der Termin wird als „rejected" markiert und die Ampel springt auf Rot. Andere Makler können dann einen neuen Termin anlegen.',
      ),
      confirmLabel: t('appointments.actions.auftrag_nicht_bekommen', 'Auftrag nicht bekommen'),
    });
    if (!ok) return;
    try {
      await update.mutateAsync({ status: 'rejected' });
      toast.success(
        t('appointments.actions.auftrag_nicht_bekommen_success', 'Status auf Rot gesetzt.'),
      );
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: t('appointments.delete.title', 'Termin löschen?'),
      body: t(
        'appointments.delete.body',
        'Der Termin wird archiviert. Admin kann ihn wiederherstellen.',
      ),
      confirmLabel: t('common.delete', 'Löschen'),
    });
    if (!ok) return;
    try {
      await archive.mutateAsync(appointment.id);
      toast.success(t('appointments.delete.success', 'Termin gelöscht.'));
      onOpenChange(false);
    } catch (cause) {
      toast.error((cause as Error).message);
    }
  };

  const dateText = formatDateTime(new Date(toMs(appointment.appointment_date)), {
    dateStyle: 'full',
    timeStyle: 'short',
  });
  const createdMs = toMs(appointment.created_at);
  const createdText = Number.isFinite(createdMs)
    ? formatDateTime(new Date(createdMs), { dateStyle: 'medium', timeStyle: 'short' })
    : '—';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('appointments.details.title', 'Termin-Details')}
          </DialogTitle>
        </DialogHeader>

        {!editing ? (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <p className="text-[11px] uppercase text-ink-3 tracking-[0.06em]">
                  {t('appointments.cols.date', 'Termin am')}
                </p>
                <p className="mt-0.5 font-medium text-ink">{dateText}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase text-ink-3 tracking-[0.06em]">
                  {t('appointments.cols.created_at', 'Erstellt am')}
                </p>
                <p className="mt-0.5 text-ink">{createdText}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] uppercase text-ink-3 tracking-[0.06em]">
                  {t('appointments.cols.type', 'Typ')}
                </p>
                <p className="mt-0.5 text-ink">
                  {t(
                    `enums.appointment_type.${appointment.appointment_type}`,
                    appointment.appointment_type,
                  )}
                </p>
              </div>
              <div>
                <p className="text-[11px] uppercase text-ink-3 tracking-[0.06em]">
                  {t('appointments.cols.status', 'Status')}
                </p>
                <p className="mt-0.5 text-ink">
                  {t(`enums.appointment_status.${appointment.status}`, appointment.status)}
                </p>
              </div>
            </div>

            <div>
              <p className="text-[11px] uppercase text-ink-3 tracking-[0.06em]">
                {t('appointments.cols.client', 'Klient')}
              </p>
              <p className="mt-0.5 inline-flex items-center gap-1.5 text-ink">
                <User className="h-3.5 w-3.5 text-ink-3" />
                {client
                  ? [client.title, client.first_name, client.last_name].filter(Boolean).join(' ')
                  : '—'}
              </p>
              {appointment.phone &&
                (isMaskedPhoneClient(appointment.phone) ? (
                  <span className="mt-0.5 ms-5 inline-flex items-center gap-1 text-xs text-ink-3">
                    <Phone className="h-3 w-3" />
                    {appointment.phone}
                  </span>
                ) : (
                  <a
                    href={`tel:${appointment.phone}`}
                    className="mt-0.5 ms-5 inline-flex items-center gap-1 text-xs text-ink-3 hover:text-ink"
                  >
                    <Phone className="h-3 w-3" />
                    {appointment.phone}
                  </a>
                ))}
            </div>

            <div>
              <p className="text-[11px] uppercase text-ink-3 tracking-[0.06em]">
                {t('appointments.cols.agent', 'Makler')}
              </p>
              <p className="mt-0.5 text-ink">{appointment.agent_full_name ?? '—'}</p>
            </div>

            <div>
              <p className="text-[11px] uppercase text-ink-3 tracking-[0.06em]">
                {t('appointments.fields.property', 'Immobilie')}
              </p>
              {property ? (
                <div className="mt-0.5 space-y-2">
                  <p className="inline-flex items-center gap-1.5 text-ink">
                    <MapPin className="h-3.5 w-3.5 text-ink-3" />
                    {[
                      property.address ??
                        `${property.street ?? ''} ${property.street_number ?? ''}`.trim(),
                      property.postal_code && property.city
                        ? `${property.postal_code} ${property.city}`
                        : property.city,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  {/* Quick status change directly from the appointment —
                      no need to open the property separately. */}
                  {canEdit && (
                    <div className="flex items-center gap-2">
                      <Label className="text-[11px] uppercase text-ink-3 tracking-[0.06em]">
                        {t('properties.fields.marketingStage', 'Vermarktungs-Phase')}
                      </Label>
                      <Select
                        className="h-8 max-w-[16rem] text-sm"
                        value={property.marketing_stage}
                        disabled={updateProperty.isPending}
                        onChange={async (e) => {
                          const next = e.target
                            .value as PropertyRow['marketing_stage'];
                          if (next === property.marketing_stage) return;
                          try {
                            await updateProperty.mutateAsync({ marketingStage: next });
                            toast.success(
                              t(
                                'properties.status_updated',
                                'Status der Immobilie aktualisiert.',
                              ),
                            );
                          } catch {
                            toast.error(
                              t('common.error', 'Etwas ist schiefgelaufen.'),
                            );
                          }
                        }}
                      >
                        {PROPERTY_STAGE_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {t(`enums.marketing_stage.${s}`, s)}
                          </option>
                        ))}
                      </Select>
                    </div>
                  )}
                </div>
              ) : (
                // Batch ZO #13 — empty state with a fast path to add the
                // property without having to enter full edit mode.
                <div className="mt-0.5 flex items-center gap-3">
                  <p className="text-ink-3 italic">
                    {t(
                      'appointments.fields.property_none',
                      'Keine Immobilie zugewiesen',
                    )}
                  </p>
                  {canEdit && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing(true)}
                    >
                      {t('appointments.actions.add_property', '+ Immobilie hinzufügen')}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {appointment.notes && (
              <div>
                <p className="text-[11px] uppercase text-ink-3 tracking-[0.06em]">
                  {t('appointments.cols.notes', 'Notiz')}
                </p>
                <p className="mt-0.5 whitespace-pre-wrap text-ink">{appointment.notes}</p>
              </div>
            )}

            {/* Auftrag-Status card — buttons ALWAYS visible, even after
                green/red. Contracts can be cancelled, corrected, or
                reverted in real workflows; every change is recorded in
                appointment_history with the previous status, new status,
                actor and timestamp (handled server-side via
                recordAppointmentChanges). */}
            {canEdit && (
              <div className="rounded-xl border border-line bg-paper-2 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <TrafficLight state={appointment.traffic_light} orientation="horizontal" />
                    <p className="font-display text-sm font-medium text-ink">
                      {t('appointments.auftrag.title', 'Auftragsstatus')}
                    </p>
                  </div>
                  {appointment.status === 'signed_contract' && (
                    <span className="fm-pill" style={{ background: '#3b8a4a', color: 'white' }}>
                      {t('appointments.auftrag.current_green', 'Aktuell: Grün')}
                    </span>
                  )}
                  {(appointment.status === 'rejected' ||
                    appointment.status === 'cancelled') && (
                    <span className="fm-pill" style={{ background: '#b04a4a', color: 'white' }}>
                      {t('appointments.auftrag.current_red', 'Aktuell: Rot')}
                    </span>
                  )}
                </div>

                <p className="mb-3 text-xs text-ink-3">
                  {t(
                    'appointments.auftrag.always_editable',
                    'Status kann jederzeit geändert werden. Jede Änderung wird im Verlauf protokolliert (alter Status, neuer Status, Bearbeiter, Datum & Uhrzeit).',
                  )}
                </p>

                {showAuftragForm ? (
                  <div className="space-y-2">
                    <Label className="text-xs">
                      {t(
                        'appointments.auftrag.edi_label',
                        'EDI-Real / Object-Number (optional, wenn Immobilie verknüpft):',
                      )}
                    </Label>
                    <Input
                      value={ediInput}
                      onChange={(e) => setEdiInput(e.target.value)}
                      placeholder="z.B. 2483/01234"
                      autoFocus
                    />
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        type="button"
                        onClick={onAuftragBekommen}
                        disabled={update.isPending || updateProperty.isPending}
                      >
                        <CheckCircle2 className="me-1.5 h-4 w-4" />
                        {t('appointments.auftrag.confirm', 'Bestätigen — Grün setzen')}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          setShowAuftragForm(false);
                          setEdiInput('');
                        }}
                      >
                        {t('common.cancel', 'Abbrechen')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      onClick={() => setShowAuftragForm(true)}
                      disabled={appointment.status === 'signed_contract'}
                      className={`${
                        appointment.status === 'signed_contract'
                          ? 'opacity-60 cursor-default'
                          : ''
                      } bg-[#3b8a4a] text-white hover:bg-[#347a40]`}
                    >
                      <CheckCircle2 className="me-1.5 h-4 w-4" />
                      {t('appointments.auftrag.bekommen', 'Auftrag bekommen')}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={onAuftragNichtBekommen}
                      disabled={
                        update.isPending ||
                        appointment.status === 'rejected' ||
                        appointment.status === 'cancelled'
                      }
                      className={`${
                        appointment.status === 'rejected' ||
                        appointment.status === 'cancelled'
                          ? 'opacity-60 cursor-default'
                          : ''
                      } text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))]`}
                    >
                      <XCircle className="me-1.5 h-4 w-4" />
                      {t('appointments.auftrag.nicht_bekommen', 'Auftrag nicht bekommen')}
                    </Button>
                    {(appointment.status === 'signed_contract' ||
                      appointment.status === 'rejected' ||
                      appointment.status === 'cancelled') && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={onAuftragZuruecksetzen}
                        disabled={update.isPending}
                        className="text-ink-2"
                        title={t(
                          'appointments.auftrag.revert_hint',
                          'Auf „offen" zurücksetzen — z.B. wenn ein Vertrag storniert wurde.',
                        )}
                      >
                        {t('appointments.auftrag.revert', 'Status zurücksetzen')}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Verlauf — full audit trail for this appointment. Every
                status change carries: previous → new, who, when. Auto-
                transitions are flagged so operators see "system" vs.
                manual flips at a glance. */}
            <div className="rounded-xl border border-line bg-card p-4">
              <p className="mb-2 text-[11px] uppercase text-ink-3 tracking-[0.06em]">
                {t('appointments.history.title', 'Verlauf')} ({history.length})
              </p>
              {history.length === 0 ? (
                <p className="text-xs text-ink-3">
                  {t('appointments.history.empty', 'Noch keine Änderungen.')}
                </p>
              ) : (
                <ul className="max-h-72 space-y-1.5 overflow-y-auto text-xs">
                  {history.map((h) => {
                    const ts = formatDateTime(new Date(h.created_at), {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    });
                    const who = h.automatic_change
                      ? t('appointments.history.system', 'System')
                      : h.changed_by_full_name ?? '—';
                    return (
                      <li
                        key={h.id}
                        className="rounded-md border border-line bg-paper-2 px-2.5 py-1.5"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-ink-2">
                            {fieldLabel('appointment', h.field_changed)}
                          </span>
                          <span className="text-ink-3">{ts}</span>
                        </div>
                        {(h.old_value || h.new_value) && (
                          <p className="mt-0.5 text-ink">
                            <span className="text-ink-3 line-through">
                              {h.old_value
                                ? valueLabel('appointment', h.field_changed, h.old_value, t)
                                : '—'}
                            </span>
                            <span className="mx-1.5 text-ink-3">→</span>
                            <span className="font-medium">
                              {h.new_value
                                ? valueLabel('appointment', h.field_changed, h.new_value, t)
                                : '—'}
                            </span>
                          </p>
                        )}
                        <p className="mt-0.5 text-ink-3">{who}</p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* v2 — document chips live here now (removed from the
                desktop row to fit everything in the viewport). The
                operator can still trigger Magic-Link/Senden from the
                dialog. */}
            <AppointmentDocuments appointmentId={appointment.id} />
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('appointments.cols.type', 'Typ')}</Label>
                <Select
                  value={form.appointmentType}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      appointmentType: e.target.value as AppointmentRow['appointment_type'],
                    }))
                  }
                >
                  {TYPE_OPTIONS.map((v) => (
                    <option key={v} value={v}>
                      {t(`enums.appointment_type.${v}`, v)}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label>{t('appointments.cols.status', 'Status')}</Label>
                <Select
                  value={form.status}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      status: e.target.value as AppointmentRow['status'],
                    }))
                  }
                >
                  {STATUS_OPTIONS.map((v) => (
                    <option key={v} value={v}>
                      {t(`enums.appointment_status.${v}`, v)}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div>
              <Label>{t('appointments.cols.date', 'Datum')}</Label>
              <Input
                type="datetime-local"
                value={form.appointmentDate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, appointmentDate: e.target.value }))
                }
              />
            </div>

            {isAdmin && (
              <div>
                <Label>{t('appointments.cols.agent', 'Makler')}</Label>
                <SearchablePicker
                  value={form.agentProfileId}
                  onChange={(v) => setForm((f) => ({ ...f, agentProfileId: v }))}
                  options={agents
                    .filter((a) => a.status === 'active')
                    .map((a) => ({
                      value: a.id,
                      label: a.full_name,
                      secondary: a.agent_id,
                    }))}
                  placeholder={t('appointments.fields.agent_pick', 'Makler wählen …')}
                />
              </div>
            )}

            <div>
              <Label>{t('appointments.fields.property', 'Immobilie')}</Label>
              <SearchablePicker
                value={form.propertyId}
                onChange={(v) => setForm((f) => ({ ...f, propertyId: v }))}
                options={properties.map((p) => ({
                  value: p.id,
                  label:
                    p.address ??
                    (`${p.street ?? ''} ${p.street_number ?? ''}`.trim() || p.id.slice(0, 8)),
                  secondary: `${p.postal_code ?? ''} ${p.city}`.trim(),
                }))}
                placeholder={t('appointments.fields.property_pick', 'Immobilie wählen …')}
              />
            </div>

            <div>
              <Label>{t('appointments.cols.notes', 'Notiz')}</Label>
              <Textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
          </div>
        )}

        <DialogFooter className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            {/* Batch ZO #8 — every user with edit access may CANCEL an
                appointment (sets status=cancelled, ostaje u istoriji);
                only admin may permanently soft-delete (archive). */}
            {canEdit && !editing && appointment.status !== 'cancelled' && (
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  const ok = await confirm({
                    title: t('appointments.cancel.title', 'Termin absagen?'),
                    body: t(
                      'appointments.cancel.body',
                      'Der Termin wird auf „abgesagt" gesetzt. Bleibt in der Historie sichtbar.',
                    ),
                    confirmLabel: t('common.cancel_action', 'Termin absagen'),
                  });
                  if (!ok) return;
                  try {
                    await update.mutateAsync({ status: 'cancelled' });
                    toast.success(
                      t('appointments.cancel.success', 'Termin abgesagt.'),
                    );
                    onOpenChange(false);
                  } catch (cause) {
                    toast.error((cause as Error).message);
                  }
                }}
                disabled={update.isPending}
              >
                <X className="me-1.5 h-4 w-4" />
                {t('common.cancel_action', 'Termin absagen')}
              </Button>
            )}
            {isAdmin && !editing && (
              <Button
                type="button"
                variant="ghost"
                onClick={onDelete}
                disabled={archive.isPending}
                className="text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger-bg))]"
              >
                <Trash2 className="me-1.5 h-4 w-4" />
                {t('common.delete', 'Löschen')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {editing ? (
              <>
                <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                  <X className="me-1.5 h-4 w-4" />
                  {t('common.cancel', 'Abbrechen')}
                </Button>
                <Button type="button" onClick={onSave} disabled={update.isPending}>
                  <Save className="me-1.5 h-4 w-4" />
                  {t('common.save', 'Speichern')}
                </Button>
              </>
            ) : (
              <>
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                  {t('common.close', 'Schließen')}
                </Button>
                {canEdit && (
                  <Button type="button" onClick={() => setEditing(true)}>
                    <Pencil className="me-1.5 h-4 w-4" />
                    {t('common.edit', 'Bearbeiten')}
                  </Button>
                )}
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
